import React from 'react';
import { Document, Font, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import InterBold from '../../../../assets/fonts/Inter-Bold.ttf';
import InterItalic from '../../../../assets/fonts/Inter-Italic.ttf';
import InterMedium from '../../../../assets/fonts/Inter-Medium.ttf';
import InterRegular from '../../../../assets/fonts/Inter-Regular.ttf';
import { formatDate } from '../../../../utils/dateHelper';
import { AddressResponse } from '../../../Restaurants/types';
import { DeliveryNoteSummary } from './deliveryNoteUtils';

type DeliveryNotePdfDocumentProps = {
  orderNumber?: string | null;
  locationName?: string | null;
  locationAddress?: AddressResponse | null;
  quickContactName: string;
  quickContactAddress?: AddressResponse | null;
  totalQuantity: number;
  summaryItems: DeliveryNoteSummary[];
  labels: {
    deliveryNoteTitle: string;
    orderNo: string;
    scanDate: string;
    summaryOfScannedItems: string;
    position: string;
    category: string;
    quantity: string;
    itemNames: string;
    itemsSection: string;
    totalNumberOfItemsPrefix: string;
    itemsReceivedNote: string;
    signatureDeliverer: string;
    signatureReceiver: string;
    signatureDate: string;
  };
};

Font.register({
  family: 'Inter',
  fonts: [
    { src: InterRegular, fontWeight: 400 },
    { src: InterMedium, fontWeight: 500 },
    { src: InterBold, fontWeight: 700 },
    { src: InterItalic, fontStyle: 'italic', fontWeight: 400 },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Inter',
    padding: 32,
    paddingTop: 64,
    fontSize: 10,
    color: '#000',
  },
  header: {
    marginBottom: 24,
  },
  restaurantAddressLine: {
    marginBottom: 8,
    fontSize: 9,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderColor: '#1A1A1A',
  },
  separator: {
    borderBottomWidth: 1,
    borderColor: '#000000',
    marginBottom: 16,
    width: '100%',
  },
  infoBlock: {
    alignSelf: 'flex-start',
  },
  quickContactInfo: {
    marginBottom: 16,
    fontSize: 11,
    fontWeight: 'bold',
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 12,
    marginTop: 8,
  },
  titleRow: {
    flexDirection: 'column',
  },
  orderInfo: {
    fontSize: 10,
    alignSelf: 'flex-end',
    textAlign: 'right',
    marginTop: 4,
  },
  orderNumber: {
    fontWeight: 'bold',
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  metaLabel: {
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  totalRow: {
    fontSize: 12,
    fontWeight: 'bold',
    marginTop: 24,
    marginBottom: 4,
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    fontSize: 10,
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: '#000',
    fontWeight: 'bold',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tableCellName: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    padding: 8,
  },
  tableCellIndex: {
    width: 30,
    padding: 8,
    textAlign: 'left',
  },
  tableCellCategory: {
    width: 120,
    padding: 8,
    textAlign: 'left',
  },
  tableCellCategoryFlex: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    padding: 8,
    textAlign: 'left',
  },
  tableCellQuantity: {
    width: 60,
    padding: 8,
    textAlign: 'left',
  },
  signatures: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  footerContainer: {
    position: 'absolute',
    left: 32,
    right: 32,
    bottom: 64,
  },
  signatureBlock: {
    width: '30%',
  },
  signatureLabel: {
    fontSize: 11,
    marginBottom: 24,
  },
  signatureLine: {
    borderBottomWidth: 1,
    borderColor: '#000',
    borderStyle: 'dashed',
    marginBottom: 4,
  },
  signatureFooter: {
    fontSize: 10,
    color: '#000',
  },
});

export const DeliveryNotePdfDocument = ({
  orderNumber,
  locationName,
  locationAddress,
  quickContactName,
  quickContactAddress,
  totalQuantity,
  summaryItems,
  labels,
}: DeliveryNotePdfDocumentProps) => {
  const hasItemNames = summaryItems.some((item) => item.items && item.items.length > 0);
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.infoBlock}>
            <Text style={styles.restaurantAddressLine} wrap={false}>
              {[locationName, locationAddress?.formattedAddress].filter(Boolean).join(', ')}
            </Text>

            <Text style={styles.quickContactInfo}>
              {quickContactName}
              {quickContactAddress ? `\n${quickContactAddress.formattedAddress?.split(',').join('\n')}` : null}
            </Text>
          </View>

          <View style={styles.titleRow}>
            <Text style={styles.title}>{labels.deliveryNoteTitle}</Text>
            <View style={styles.orderInfo}>
              <View>
                {orderNumber ? (
                  <Text>
                    {labels.orderNo} <Text style={styles.orderNumber}>{orderNumber}</Text>
                  </Text>
                ) : null}
                <Text>
                  {labels.scanDate} {formatDate(new Date())}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <Text style={styles.sectionTitle}>{labels.summaryOfScannedItems}</Text>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.tableCellIndex}>{labels.position}</Text>
            <Text style={hasItemNames ? styles.tableCellCategory : styles.tableCellCategoryFlex}>
              {labels.category}
            </Text>
            <Text style={styles.tableCellQuantity}>{labels.quantity}</Text>
            {hasItemNames && (
              <Text style={styles.tableCellName} wrap>
                {labels.itemNames}
              </Text>
            )}
          </View>
          {summaryItems.map((item, index) => (
            <View key={item.category} style={styles.tableRow}>
              <Text style={styles.tableCellIndex}>{index + 1}</Text>
              <Text style={hasItemNames ? styles.tableCellCategory : styles.tableCellCategoryFlex}>
                {item.category}
              </Text>
              <Text style={styles.tableCellQuantity}>{item.quantity}</Text>
              {hasItemNames && (
                <Text style={styles.tableCellName} wrap>
                  {item.items?.map((i) => i.name).join(', ') || '-'}
                </Text>
              )}
            </View>
          ))}
        </View>

        <Text style={styles.totalRow}>
          {labels.totalNumberOfItemsPrefix} {totalQuantity}
        </Text>

        <View style={{ height: 120 }} />

        <View style={styles.footerContainer} fixed>
          <Text style={styles.signatureLabel}>{labels.itemsReceivedNote}</Text>

          <View style={styles.signatures}>
            <View style={styles.signatureBlock}>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureFooter}>{labels.signatureDate}</Text>
            </View>
            <View style={styles.signatureBlock}>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureFooter}>{labels.signatureDeliverer}</Text>
            </View>
            <View style={styles.signatureBlock}>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureFooter}>{labels.signatureReceiver}</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export type { DeliveryNotePdfDocumentProps };
